import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { cn } from '@/v2/utils';
import { usesSwipeablePricingCards } from '@/v2/canUseV2';
import { MODEL_CONFIGS } from '@/v2/data/models';
import {
  Check,
  X,
  Info,
  Sparkles,
  Lock,
  CreditCard,
  Mail,
  Building2,
  UsersRound,
  Star,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  Quote,
  Loader2,
  Users,
  Minus,
  Plus,
  MoveHorizontal,
  Clock,
} from 'lucide-react';
// Real checkout plumbing (live Stripe price IDs + amounts from the shared V1
// config — single source of truth for the whole pricing schema).
import { apiService, ExitOfferExpiredError } from '@/api/apiService';
import { PRO_PLAN as PRO_CONFIG, MAX_PLAN as MAX_CONFIG, TEAM_PLAN, teamCreditTierSavings } from '@/api/config';
import { useUserStore } from '@/store/userStore';
import { useExitOfferStore } from '@/store/exitOfferStore';
import { Countdown } from '@/features/subscription/ExitOffer';
import { isPaidPlan } from '@/utils/planHelpers';
import { toast } from '@/v2/toast';
import { useDirectPayment, clearDirectPaymentFailureFlag } from '@/components/shared/EmbeddedCheckoutModal';
import { CreateTeamModal } from '@/v2/components/CreateTeamModal';
import { AutoReloadCard } from '@/v2/components/AutoReloadCard';
// V1 analytics (read-only import) — keeps conversion tracking identical in V2.
import {
  trackPaywallShown,
  trackBeginCheckout,
  trackPurchase,
  trackCheckoutCanceled,
  trackCreditsPurchase,
  trackBillingPeriodToggle,
  trackEmailOfferViewed,
} from '@/services/analyticsService';
import {
  captureEmailAttribution,
  getEmailAttribution,
  clearEmailAttribution,
  emailAttributionEventProps,
  captureEmailOffer,
  getEmailOffer,
  type EmailOfferId,
} from '@/services/emailAttribution';

// V1 pricing schema, unchanged: PRO $9/wk · $99/yr ($2/wk) · 30 credits/wk;
// MAX $19/wk · $199/yr ($4/wk) · 100 credits/wk. Amounts and Stripe price ids
// all come from the shared config — no values are duplicated here.
const PRO_PLAN = PRO_CONFIG;
const MAX_PLAN = MAX_CONFIG;

// First-week price per recovery-email offer, used only if the status omits price_cents.
const EMAIL_OFFER_FALLBACK_CENTS: Record<EmailOfferId, number> = { email_699: 699, email_499: 499, email_199: 199 };

const PLAN_PRICE_IDS: Record<'pro' | 'max', { weekly: string; yearly: string }> = {
  pro: { weekly: PRO_CONFIG.weeklyPriceId, yearly: PRO_CONFIG.yearlyPriceId },
  max: { weekly: MAX_CONFIG.weeklyPriceId, yearly: MAX_CONFIG.yearlyPriceId },
};

// One-time credit top-ups (paid users) — V1 rates, unchanged.
const CREDITS_PER_PACK = 10; // pricing unit ($2 / 10 credits)
const PRICE_PER_PACK = 2;
const CREDIT_STEP = 50; // +/- stepper amount
const MIN_CREDITS = 50;
const MAX_CREDITS = 100000;
const DEFAULT_CREDITS = 200;

// Social-proof counter — a GLOBAL weekly figure, not a per-browser random one.
// The base is derived purely from the clock: 5,000 at the start of Monday, rising
// linearly to 8,000 by the end of Sunday, so every visitor sees ~the same number
// at the same moment and it matches the "started this week" copy. It resets each
// Monday (intended — it's a weekly count) but is monotonic WITHIN the week: we
// persist the week's last-shown value so reopening never jumps backward. On top of
// the base it ticks +1–3 every 1–4s for live feel, clamped to the 8,000 ceiling.
const SIGNUP_MIN = 5000;
const SIGNUP_MAX = 8000;
const SIGNUP_STORE_KEY = 'vv_signup';
const WEEK_MS = 7 * 24 * 3_600_000;

// Base + a stable id for the current week, measured from Monday 00:00 UTC — so
// every visitor worldwide shares the exact same weekly figure at the same instant.
function weekInfo(): { weekId: string; base: number } {
  const now = new Date();
  const monday = new Date(now);
  monday.setUTCHours(0, 0, 0, 0);
  monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7)); // Mon=0 … Sun=6
  const frac = Math.min(1, Math.max(0, (now.getTime() - monday.getTime()) / WEEK_MS));
  return {
    weekId: `${monday.getUTCFullYear()}-${monday.getUTCMonth() + 1}-${monday.getUTCDate()}`,
    base: SIGNUP_MIN + Math.round((SIGNUP_MAX - SIGNUP_MIN) * frac),
  };
}

function computeSignupBase(weekId: string, base: number): number {
  let start = base;
  try {
    const raw = localStorage.getItem(SIGNUP_STORE_KEY);
    if (raw) {
      const s = JSON.parse(raw) as { weekId: string; c: number };
      // Same week → never show less than we last showed. New week → reset to base.
      if (s.weekId === weekId && typeof s.c === 'number') start = Math.max(base, s.c);
    }
  } catch { /* ignore */ }
  start = Math.min(SIGNUP_MAX, start);
  try { localStorage.setItem(SIGNUP_STORE_KEY, JSON.stringify({ weekId, c: start })); } catch { /* ignore */ }
  return start;
}

function LiveSignupCounter() {
  const { t } = useTranslation('v2');
  const weekIdRef = useRef(weekInfo().weekId);
  const [count, setCount] = useState(() => {
    const { weekId, base } = weekInfo();
    weekIdRef.current = weekId;
    return computeSignupBase(weekId, base);
  });
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      setCount((c) => {
        const next = Math.min(SIGNUP_MAX, c + 1 + Math.floor(Math.random() * 3)); // +1..+3, capped
        try { localStorage.setItem(SIGNUP_STORE_KEY, JSON.stringify({ weekId: weekIdRef.current, c: next })); } catch { /* ignore */ }
        return next;
      });
      timer = setTimeout(tick, 1000 + Math.random() * 3000);  // irregular 1–4s
    };
    timer = setTimeout(tick, 1000 + Math.random() * 3000);
    return () => clearTimeout(timer);
  }, []);
  // Emphasize just the number (bold + underlined) regardless of where it falls in
  // the translated sentence: interpolate a unique sentinel, split on it, wrap the
  // middle piece in a styled <strong>.
  const SENTINEL = '␟';
  const [before, after] = t('upgrade.socialProof', { total: SENTINEL }).split(SENTINEL);
  return (
    <span>
      {before}
      <strong className="font-extrabold text-text-primary underline decoration-primary decoration-2 underline-offset-2 tabular-nums">
        {count.toLocaleString()}
      </strong>
      {after ?? ''}
    </span>
  );
}

const newTxnId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

// ---------------------------------------------------------------------------
// Plan feature model — a single ordered list (most → least important) rendered
// by BOTH cards so PRO/MAX line up for comparison. `important` features show by
// default; the rest sit behind a "See all features" expander. `tipKey` features
// render with a zig-zag underline + an animated explainer popup (FeatureTooltip).
// `maxOnly` features render a green check on MAX and a muted X on PRO.
// ---------------------------------------------------------------------------
interface Feature {
  /** i18n key (under `upgrade`) for the row label. */
  key: string;
  /** i18n key for the explainer popup body — presence makes the row hoverable. */
  tipKey?: string;
  /** Visible by default (vs. behind the "See all features" expander). */
  important?: boolean;
  /** Available on MAX only → check on MAX, X on PRO, "Coming soon" pill. */
  maxOnly?: boolean;
  /** Route the popup's "Learn more" link opens (maxOnly rows). */
  link?: string;
  /** Popup also lists the real model lineup (from MODEL_CONFIGS). */
  models?: boolean;
}

// Most important → least important. The same list renders on both cards so the
// rows line up side-by-side for easy PRO vs MAX comparison.
const COMMON_FEATURES: Feature[] = [
  { key: 'featLongForm', tipKey: 'tipLongForm' },
  { key: 'featModels', tipKey: 'tipModels', models: true },
  { key: 'featAvatars', tipKey: 'tipAvatars' },
  { key: 'featVoices', tipKey: 'tipVoices' },
  { key: 'featCloneAvatar', tipKey: 'tipCloneAvatar' },
  { key: 'featCloneVoice', tipKey: 'tipCloneVoice' },
  { key: 'featTemplates', tipKey: 'tipTemplates' },
  { key: 'featDesignAvatar', tipKey: 'tipDesignAvatar' },
  { key: 'featDesignVoice', tipKey: 'tipDesignVoice' },
  { key: 'featBrandKit', tipKey: 'tipBrandKit' },
  { key: 'featAttachFiles', tipKey: 'tipAttachFiles' },
  { key: 'benefitHdExports', tipKey: 'tipHdExports' },
  { key: 'benefitNoWatermarks', tipKey: 'tipNoWatermarks' },
  { key: 'benefitDownloadShare', tipKey: 'tipDownloadShare' },
  { key: 'benefitExtend', tipKey: 'tipExtend' },
  { key: 'benefitEditPrompt', tipKey: 'tipEditPrompt' },
  { key: 'benefitRemix', tipKey: 'tipRemix' },
];

const MAX_FEATURES: Feature[] = [
  { key: 'featPublish', tipKey: 'tipPublish', maxOnly: true, link: '/integrations' },
  { key: 'featAutomations', tipKey: 'tipAutomations', maxOnly: true, link: '/automations' },
  { key: 'featMcp', tipKey: 'tipMcp', maxOnly: true, link: '/developers' },
  { key: 'featApi', tipKey: 'tipApi', maxOnly: true, link: '/developers' },
];

// Review sources — one switchable, brand-colored tab per platform, each with
// several specific 5-star reviews and the platform's aggregate rating.
interface PlatformReview { author: string; avatar: string; titleKey: string; textKey: string }
interface ReviewPlatform {
  id: string;
  name: string;
  /** Aggregate rating shown as a partially-filled star bar. */
  rating: number;
  /** Brand accent color (tab underline, quote mark, card top border). */
  color: string;
  icon: string;
  url: string;
  reviews: PlatformReview[];
}

const REVIEW_PLATFORMS: ReviewPlatform[] = [
  {
    id: 'trustpilot',
    name: 'Trustpilot',
    rating: 4.8,
    color: '#00B67A',
    icon: 'https://www.google.com/s2/favicons?domain=trustpilot.com&sz=64',
    url: 'https://www.trustpilot.com/review/vivideo.ai',
    reviews: [
      { author: 'Dave', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', titleKey: 'reviewTrustpilot1Title', textKey: 'reviewTrustpilot1' },
      { author: 'Sophie L.', avatar: 'https://randomuser.me/api/portraits/women/44.jpg', titleKey: 'reviewTrustpilot2Title', textKey: 'reviewTrustpilot2' },
      { author: 'Mark R.', avatar: 'https://randomuser.me/api/portraits/men/46.jpg', titleKey: 'reviewTrustpilot3Title', textKey: 'reviewTrustpilot3' },
    ],
  },
  {
    id: 'googleplay',
    name: 'Google Play',
    rating: 4.7,
    color: '#34A853',
    icon: 'https://www.google.com/s2/favicons?domain=play.google.com&sz=64',
    url: 'https://play.google.com/store/apps/details?id=com.vivideo.ai',
    reviews: [
      { author: 'Delena Delich', avatar: 'https://randomuser.me/api/portraits/women/68.jpg', titleKey: 'reviewGooglePlay1Title', textKey: 'reviewGooglePlay1' },
      { author: 'Kyle Ramos', avatar: 'https://randomuser.me/api/portraits/men/54.jpg', titleKey: 'reviewGooglePlay2Title', textKey: 'reviewGooglePlay2' },
      { author: 'Priya S.', avatar: 'https://randomuser.me/api/portraits/women/65.jpg', titleKey: 'reviewGooglePlay3Title', textKey: 'reviewGooglePlay3' },
    ],
  },
  {
    id: 'appstore',
    name: 'App Store',
    rating: 4.8,
    color: '#0A84FF',
    icon: 'https://www.google.com/s2/favicons?domain=apple.com&sz=64',
    url: 'https://apps.apple.com/us/app/vivideo-ai-ai-video-generator/id6755797079',
    reviews: [
      { author: 'Ligonov', avatar: 'https://randomuser.me/api/portraits/men/67.jpg', titleKey: 'reviewAppStore1Title', textKey: 'reviewAppStore1' },
      { author: 'Marco P.', avatar: 'https://randomuser.me/api/portraits/men/76.jpg', titleKey: 'reviewAppStore2Title', textKey: 'reviewAppStore2' },
      { author: 'Hannah B.', avatar: 'https://randomuser.me/api/portraits/women/33.jpg', titleKey: 'reviewAppStore3Title', textKey: 'reviewAppStore3' },
    ],
  },
  {
    id: 'capterra',
    name: 'Capterra',
    rating: 5.0,
    color: '#FF9D28',
    icon: 'https://www.google.com/s2/favicons?domain=capterra.com&sz=64',
    url: 'https://www.capterra.com/p/10034820/Vivideo/',
    reviews: [
      { author: 'Amanda K.', avatar: 'https://randomuser.me/api/portraits/women/22.jpg', titleKey: 'reviewCapterra1Title', textKey: 'reviewCapterra1' },
      { author: 'Daniel R.', avatar: 'https://randomuser.me/api/portraits/men/85.jpg', titleKey: 'reviewCapterra2Title', textKey: 'reviewCapterra2' },
      { author: 'Olivia P.', avatar: 'https://randomuser.me/api/portraits/women/57.jpg', titleKey: 'reviewCapterra3Title', textKey: 'reviewCapterra3' },
    ],
  },
  {
    id: 'g2',
    name: 'G2',
    rating: 4.9,
    color: '#FF492C',
    icon: 'https://www.google.com/s2/favicons?domain=g2.com&sz=64',
    url: 'https://www.g2.com/products/vivideo/reviews?source=search#reviews',
    reviews: [
      { author: 'Sarah C.', avatar: 'https://randomuser.me/api/portraits/women/90.jpg', titleKey: 'reviewG2_1Title', textKey: 'reviewG2_1' },
      { author: 'Marcus R.', avatar: 'https://randomuser.me/api/portraits/men/12.jpg', titleKey: 'reviewG2_2Title', textKey: 'reviewG2_2' },
      { author: 'Emily W.', avatar: 'https://randomuser.me/api/portraits/women/47.jpg', titleKey: 'reviewG2_3Title', textKey: 'reviewG2_3' },
    ],
  },
];

// Trustpilot is surfaced as a top-of-page badge that jumps to the reviews.
const TRUSTPILOT = REVIEW_PLATFORMS.find((p) => p.id === 'trustpilot')!;

// Temporarily hide the Enterprise contact banner from the pricing page (free
// users). Set back to true to bring it back.
const SHOW_ENTERPRISE_BANNER = false;

const ENTERPRISE_FEATURES = [
  i18n.t('upgrade.enterpriseUnlimitedCredits', { ns: 'v2' }),
  i18n.t('upgrade.enterpriseTeamSeats', { ns: 'v2' }),
  i18n.t('upgrade.enterpriseApiAccess', { ns: 'v2' }),
  i18n.t('upgrade.enterpriseSso', { ns: 'v2' }),
  i18n.t('upgrade.enterpriseIntegrations', { ns: 'v2' }),
  i18n.t('upgrade.enterprisePrioritySupport', { ns: 'v2' }),
  i18n.t('upgrade.enterpriseSla', { ns: 'v2' }),
  i18n.t('upgrade.enterpriseInvoicing', { ns: 'v2' }),
];

// Accepted payment methods — official card-brand artwork (flat color tiles).
export const PAYMENT_LOGOS = [
  { name: 'Visa', src: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/visa.svg' },
  { name: 'Mastercard', src: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/mastercard.svg' },
  { name: 'American Express', src: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/amex.svg' },
  { name: 'Discover', src: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/discover.svg' },
  { name: 'PayPal', src: 'https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@main/flat/paypal.svg' },
];

// FAQ reuses the already-translated V1 (classic) keys from the shared
// 'translation' namespace so we don't re-translate the same content for V2.
export const FAQS = [
  {
    q: i18n.t('subscription.faqCreditsQ', { ns: 'translation' }),
    a: i18n.t('subscription.faqCreditsA', { ns: 'translation' }),
  },
  {
    q: i18n.t('subscription.faqCancelQ', { ns: 'translation' }),
    a: i18n.t('subscription.faqCancelA', { ns: 'translation' }),
  },
  {
    q: i18n.t('subscription.faqDiffQ', { ns: 'translation' }),
    a: i18n.t('subscription.faqDiffA', { ns: 'translation' }),
  },
  {
    q: i18n.t('subscription.faqPaymentQ', { ns: 'translation' }),
    a: i18n.t('subscription.faqPaymentA', { ns: 'translation' }),
  },
  {
    q: i18n.t('subscription.faqPaymentFailQ', { ns: 'translation' }),
    a: i18n.t('subscription.faqPaymentFailA', { ns: 'translation' }),
  },
  {
    q: i18n.t('subscription.faqRefundQ', { ns: 'translation' }),
    a: i18n.t('subscription.faqRefundA', { ns: 'translation' }),
  },
];

export function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border border-glass-border rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-surface-light/50 transition-colors"
      >
        <span className="text-sm font-medium text-text-primary pr-4">{question}</span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-text-tertiary flex-shrink-0 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-200',
          isOpen ? 'max-h-48 pb-4 px-4' : 'max-h-0'
        )}
      >
        <p className="text-sm text-text-secondary leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

/** Real enterprise inquiry — posts to the live /enterprise-contact endpoint
 *  (sends an email to support@vivideo.ai via Resend). */
function EnterpriseModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('v2');
  const user = useUserStore((s) => s.user);
  const [name, setName] = useState(user?.displayName || user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [volume, setVolume] = useState<'1-99' | '100-999' | '1000+'>('100-999');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || !email.trim() || !message.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      await apiService.submitEnterpriseContact({
        name: name.trim(), email: email.trim(), monthlyVideoCount: volume,
        message: message.trim(), source: 'webapp-v2-upgrade',
      });
      setSent(true);
    } catch {
      setError(t('upgrade.enterpriseSendError'));
    } finally { setBusy(false); }
  };

  const field = 'w-full bg-surface-light border border-glass-border rounded-xl px-4 py-3 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:border-primary/50';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-surface border border-glass-border p-6 shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center">
              <Check className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-bold text-text-primary mb-1">{t('upgrade.requestSent')}</h3>
            <p className="text-sm text-text-secondary mb-5">{t('upgrade.requestSentDesc')}</p>
            <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold">{t('upgrade.done')}</button>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-bold text-text-primary mb-4">{t('upgrade.talkToSales')}</h3>
            <div className="space-y-3">
              <input className={field} placeholder={t('upgrade.yourName')} value={name} onChange={(e) => setName(e.target.value)} />
              <input className={field} placeholder={t('upgrade.workEmail')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <select className={field} value={volume} onChange={(e) => setVolume(e.target.value as typeof volume)}>
                <option value="1-99">{t('upgrade.volume199')}</option>
                <option value="100-999">{t('upgrade.volume100999')}</option>
                <option value="1000+">{t('upgrade.volume1000plus')}</option>
              </select>
              <textarea className={field + ' resize-none'} rows={3} placeholder={t('upgrade.useCasePlaceholder')} value={message} onChange={(e) => setMessage(e.target.value)} />
              {error && <p className="text-xs text-amber-300/80">{error}</p>}
              <button
                onClick={() => void submit()}
                disabled={busy || !name.trim() || !email.trim() || !message.trim()}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold disabled:opacity-50"
              >
                {busy ? t('upgrade.sending') : t('upgrade.sendRequest')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FeatureTooltip — a label with a zig-zag (wavy) underline that reveals an
// animated explainer popup. Opens on hover/focus (desktop) and toggles on tap
// (mobile); closes on outside tap / Escape. `models` rows list the real model
// lineup; `maxOnly` rows surface a "Learn more" deep-link to the feature page.
// ---------------------------------------------------------------------------
function FeatureTooltip({
  feature,
  muted,
  label,
  tip,
  labelClassName,
}: {
  feature: Feature;
  muted?: boolean;
  /** Overrides the i18n label (e.g. for the dynamic credits row). */
  label?: React.ReactNode;
  /** Overrides the i18n tip text. */
  tip?: string;
  labelClassName?: string;
}) {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const open = hover || pinned;
  const labelNode = label ?? t(`upgrade.${feature.key}`);
  const tipText = tip ?? t(`upgrade.${feature.tipKey}`);

  useEffect(() => {
    if (!pinned) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setPinned(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setPinned(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onEsc); };
  }, [pinned]);

  return (
    <span
      ref={ref}
      className="relative inline-flex"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <button
        type="button"
        aria-expanded={open}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        onClick={() => setPinned((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1 text-left underline decoration-wavy decoration-1 underline-offset-4 transition-colors',
          muted
            ? 'text-text-tertiary decoration-white/[0.06]'
            : 'decoration-white/[0.08] hover:decoration-white/25',
          labelClassName,
        )}
      >
        <span>{labelNode}</span>
        <Info className={cn('w-3 h-3 flex-shrink-0', muted ? 'text-text-tertiary/40' : 'text-text-tertiary/70')} />
      </button>
      {open && (
        <span className="absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2">
          <span
            role="tooltip"
            onClick={(e) => e.stopPropagation()}
            className="block w-[270px] max-w-[78vw] rounded-2xl border border-primary/30 bg-[#15151d] p-4 shadow-2xl animate-slide-up cursor-default"
          >
            <span className="mb-1.5 flex flex-wrap items-center gap-1.5 text-sm font-bold text-text-primary">
              <Sparkles className="w-3.5 h-3.5 flex-shrink-0 text-primary" />
              {labelNode}
              {feature.maxOnly && (
                <span className="inline-flex items-center rounded-full border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-300">
                  {t('upgrade.comingSoon')}
                </span>
              )}
            </span>
            <span className="block text-xs leading-relaxed text-text-secondary">{tipText}</span>
            {feature.models && (
              <span className="mt-3 flex flex-wrap gap-1.5">
                {MODEL_CONFIGS.slice(0, 14).map((m) => (
                  <span key={m.id} className="inline-flex items-center gap-1 rounded-md border border-primary/25 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                    <img src={m.brandIcon} alt="" className="h-3 w-3 rounded-sm object-contain" />
                    {m.name}
                  </span>
                ))}
              </span>
            )}
            {feature.maxOnly && feature.link && (
              <button
                type="button"
                onClick={() => { setPinned(false); setHover(false); navigate(feature.link!); }}
                className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary-light"
              >
                {t('upgrade.learnMore')} <ArrowUpRight className="w-3 h-3" />
              </button>
            )}
          </span>
        </span>
      )}
    </span>
  );
}

// Wraps the credit amount in the brand gradient (matching V1 classic styling)
// while keeping the rest of the translated/interpolated string intact.
function colorizeCredits(text: string, credits: number): React.ReactNode {
  const token = String(credits);
  const idx = text.indexOf(token);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="bg-gradient-to-r from-primary to-accent bg-clip-text font-bold text-transparent">{token}</span>
      {text.slice(idx + token.length)}
    </>
  );
}

// One feature line: check (available) / muted X (maxOnly on PRO), the label
// (plain or as a hoverable FeatureTooltip), and a "Coming soon" pill for maxOnly.
function FeatureRow({ feature, isMax }: { feature: Feature; isMax: boolean }) {
  const { t } = useTranslation('v2');
  const available = !feature.maxOnly || isMax;
  return (
    <div className="flex items-start gap-2.5">
      {available ? (
        <Check className="mt-0.5 w-4 h-4 flex-shrink-0 text-green-500" />
      ) : (
        <X className="mt-0.5 w-4 h-4 flex-shrink-0 text-red-500" />
      )}
      <span className={cn('flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm', available ? 'text-text-primary' : 'text-text-tertiary')}>
        {feature.tipKey
          ? <FeatureTooltip feature={feature} muted={!available} />
          : <span>{t(`upgrade.${feature.key}`)}</span>}
      </span>
    </div>
  );
}

// Secure-checkout strip (Lock + accepted cards) + the Trustpilot rating badge.
// Shared verbatim by every plan card and the exit-offer card so they stay
// pixel-identical.
export function CheckoutTrustStrip() {
  const { t } = useTranslation('v2');
  return (
    <>
      {/* Mini trust strip — secure checkout + accepted cards, right at the CTA */}
      <div className="mb-5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5">
        <span className="inline-flex items-center gap-1 text-[11px] text-text-tertiary">
          <Lock className="w-3 h-3" /> {t('upgrade.secureCheckout')}
        </span>
        <span className="hidden text-text-tertiary/40 sm:inline">·</span>
        <span className="flex items-center gap-1">
          {PAYMENT_LOGOS.slice(0, 4).map((logo) => (
            <img
              key={logo.name}
              src={logo.src}
              alt={logo.name}
              title={logo.name}
              loading="lazy"
              className="h-4 w-auto rounded-[3px] ring-1 ring-glass-border"
            />
          ))}
        </span>
      </div>
      {/* Trustpilot badge — sits between the secure-checkout strip and the
          feature bullets (owner, 2026-07-08). */}
      <div className="mb-5 flex items-center justify-center gap-1.5">
        <img src={TRUSTPILOT.icon} alt="Trustpilot" className="h-4 w-4 rounded-sm" />
        <span className="text-xs font-semibold text-text-secondary">Trustpilot</span>
        <span className="flex items-center gap-0.5">
          {[...Array(5)].map((_, i) => (
            <span key={i} className="flex h-3.5 w-3.5 items-center justify-center rounded-[2px] bg-[#00B67A]">
              <Star className="h-2 w-2 fill-white text-white" />
            </span>
          ))}
        </span>
        <span className="text-xs font-semibold text-text-primary">{TRUSTPILOT.rating.toFixed(1)}</span>
      </div>
    </>
  );
}

// The full feature bullet list shared by the PRO/MAX cards and the exit-offer
// card: the dynamic headline credits row, the first 7 common features, then the
// rest + the MAX-only section behind a "See all features" accordion.
export function PlanFeatureBullets({
  credits,
  videoCount,
  isMax,
}: {
  credits: number;
  videoCount: number;
  isMax: boolean;
}) {
  const { t } = useTranslation('v2');
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const PRIMARY_COUNT = 7;
  const primaryFeatures = COMMON_FEATURES.slice(0, PRIMARY_COUNT);
  const restFeatures = COMMON_FEATURES.slice(PRIMARY_COUNT);

  return (
    <div className="space-y-2.5 flex-1 cursor-default" onClick={(e) => e.stopPropagation()}>
      {/* Headline credits row (dynamic, hoverable) — credit amount in the
          brand gradient, matching V1 (classic) styling. */}
      <div className="flex items-start gap-2.5">
        <Check className="mt-0.5 w-5 h-5 flex-shrink-0 text-green-500" />
        <span className="text-base font-semibold text-text-primary">
          <FeatureTooltip
            feature={{ key: 'benefitCreditsVideos', tipKey: 'tipCredits' }}
            label={colorizeCredits(t('upgrade.benefitCreditsVideos', { credits, videoCount }), credits)}
            tip={t('upgrade.tipCredits', { credits })}
            labelClassName="text-base font-semibold text-text-primary"
          />
        </span>
      </div>

      {/* First 7 common features → 8 bullets shown with the headline above. */}
      {primaryFeatures.map((f) => <FeatureRow key={f.key} feature={f} isMax={isMax} />)}

      {/* Accordion: the rest of the common features + the MAX-only section. */}
      <div className={cn('grid transition-[grid-template-rows] duration-300 ease-out', showAllFeatures ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
        <div className="overflow-hidden min-h-0">
          <div className="space-y-2.5 pt-0.5">
            {restFeatures.map((f) => <FeatureRow key={f.key} feature={f} isMax={isMax} />)}
            <div className="mt-1 border-t border-glass-border pt-3">
              <p className="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-text-tertiary">
                {t('upgrade.onlyOnMax')}
              </p>
              <div className="space-y-2.5">
                {MAX_FEATURES.map((f) => <FeatureRow key={f.key} feature={f} isMax={isMax} />)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); setShowAllFeatures((v) => !v); }}
        className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
      >
        {showAllFeatures ? t('upgrade.showFewerFeatures') : t('upgrade.showAllFeatures')}
        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', showAllFeatures && 'rotate-180')} />
      </button>
    </div>
  );
}

interface PlanCardProps {
  name: string;
  description: string;
  plan: { weeklyAmount: number; yearlyAmount: number; yearlyPerWeek: number; credits: number };
  videoCount: number;
  billingPeriod: 'weekly' | 'yearly';
  popular?: boolean;
  isMax: boolean;
  onSwitchYearly: () => void;
  onSelect: () => void;
  loading?: boolean;
  // Live recovery-email offer (weekly only): first-week price + server deadline.
  offer?: { price: string; expiresAt: number; onExpire: () => void };
}

function PlanCard({
  name,
  description,
  plan,
  videoCount,
  billingPeriod,
  popular,
  isMax,
  onSwitchYearly,
  onSelect,
  loading,
  offer: offerProp,
}: PlanCardProps) {
  const { t } = useTranslation('v2');
  const price = billingPeriod === 'yearly' ? plan.yearlyPerWeek : plan.weeklyAmount;
  const offer = billingPeriod === 'weekly' ? offerProp : undefined;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={t('upgrade.startCreating')}
      onClick={() => { if (!loading) onSelect(); }}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !loading) { e.preventDefault(); onSelect(); } }}
      className={cn(
        'relative rounded-2xl bg-surface flex flex-col cursor-pointer transition-all duration-200 hover:shadow-lg',
        offer
          ? 'border-2 border-emerald-500/40 hover:border-emerald-500/60'
          : popular
          ? 'border-2 border-primary/30 hover:border-primary/50'
          : 'border border-glass-border hover:border-primary/40'
      )}
    >
      {offer && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
          <span className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold whitespace-nowrap">
            {t('exitOffer.limitedOffer')}
          </span>
        </div>
      )}
      {popular && (
        <div className="absolute -top-3 right-4 z-10">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary text-white text-xs font-bold shadow-lg">
            <Sparkles className="w-3 h-3" />
            {t('upgrade.mostPopular')}
          </span>
        </div>
      )}
      <div className="p-6 md:p-8 flex-1 flex flex-col">
        {/* min-h on the description keeps the header block the same height on
            every card, so the price rows + CTAs align across the grid. */}
        <div className="mb-5">
          <h3 className="text-2xl font-bold text-text-primary mb-1">{name}</h3>
          <p className="text-sm text-text-secondary min-h-10">{description}</p>
        </div>
        {offer ? (
          /* Same price + timer presentation as the exit-offer card. */
          <div className="mb-5">
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl text-text-tertiary line-through">${plan.weeklyAmount}</span>
              <span className="text-4xl font-bold text-emerald-500">${offer.price}</span>
              <span className="text-text-secondary text-sm">{t('exitOffer.firstWeekLabel')}</span>
            </div>
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900 border border-emerald-500/30">
              <Clock className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs font-medium text-emerald-300">{t('exitOffer.expiresIn')}</span>
              <Countdown expiresAt={offer.expiresAt} size="sm" format="hms" onExpire={offer.onExpire} />
            </div>
          </div>
        ) : (
        <div className="mb-5">
          <div className="flex items-baseline gap-1.5">
            <span className="text-4xl font-bold text-text-primary">${price}</span>
            <span className="text-text-secondary text-sm">{t('upgrade.perWeek')}</span>
          </div>
          <div className="h-5 mt-1.5">
            {billingPeriod === 'weekly' && (
              <button
                onClick={(e) => { e.stopPropagation(); onSwitchYearly(); }}
                className="inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-500 underline underline-offset-2 decoration-green-600/40 transition-colors"
              >
                <span>
                  {t('upgrade.switchYearlySave', { amount: plan.weeklyAmount - plan.yearlyPerWeek })}
                </span>
                <ArrowUpRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
        )}
        <p className="text-xs text-text-secondary text-center mb-2">{t('upgrade.cancelAnytime')}</p>
        <button
          onClick={(e) => { e.stopPropagation(); onSelect(); }}
          disabled={loading}
          className={cn(
            'w-full py-3.5 mb-1 rounded-xl text-white text-base font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2',
            offer ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-primary hover:bg-primary/90'
          )}
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {loading ? t('upgrade.openingCheckout') : offer ? t('exitOffer.claimCta') : t('upgrade.startCreating')}
        </button>
        <p className="text-xs text-text-secondary text-center mb-2 h-4">
          {billingPeriod === 'yearly' ? t('upgrade.billedYearlyAt', { amount: plan.yearlyAmount }) : ''}
        </p>
        <CheckoutTrustStrip />
        {/* Bullet points carry their own hover/tap tooltips & links, so clicks
            here must NOT bubble up to the card-as-CTA handler. */}
        <PlanFeatureBullets credits={plan.credits} videoCount={videoCount} isMax={isMax} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TeamPlanCard — the Team workspace plan. Visually IDENTICAL to PlanCard
// (badge, price, CTA, trust strip, Trustpilot, hoverable benefit bullets);
// the only additions are the two compact configurator rows under the price:
// total seats (2 included) and the shared weekly credit pool (bigger tiers
// priced below linear — the save-% chip nudges upsizing). Defaults anchor on
// the lowest price (2 seats / 200 credits) and the price updates live.
// ---------------------------------------------------------------------------
interface TeamPlanCardProps {
  billingPeriod: 'weekly' | 'yearly';
  onSwitchYearly: () => void;
  onCreate: (config: { billingPeriod: 'weekly' | 'yearly'; seats: number; credits: number }) => void;
}

function TeamPlanCard({ billingPeriod, onSwitchYearly, onCreate }: TeamPlanCardProps) {
  const { t } = useTranslation('v2');
  const [seats, setSeats] = useState<number>(TEAM_PLAN.includedSeats);
  const [credits, setCredits] = useState<number>(TEAM_PLAN.baseCredits);
  // Same bullets machinery as PlanCard, but only 2 features shown by default:
  // the configurator above eats ~5 bullet rows of height, so this keeps the
  // "See all features" button vertically aligned with the PRO/MAX cards.
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const PRIMARY_COUNT = 2;
  const primaryFeatures = COMMON_FEATURES.slice(0, PRIMARY_COUNT);
  const restFeatures = COMMON_FEATURES.slice(PRIMARY_COUNT);

  const extraSeats = Math.max(0, seats - TEAM_PLAN.includedSeats);
  const weeklyPrice =
    billingPeriod === 'weekly'
      ? TEAM_PLAN.weekly.creditTierTotals[credits] + extraSeats * TEAM_PLAN.weekly.seat
      : TEAM_PLAN.yearly.creditTierTotalsPerWeek[credits] + extraSeats * TEAM_PLAN.yearly.seatPerWeek;
  const yearlyTotal = TEAM_PLAN.yearly.creditTierTotals[credits] + extraSeats * TEAM_PLAN.yearly.seat;
  const weeklyBaseTotal = TEAM_PLAN.weekly.creditTierTotals[credits] + extraSeats * TEAM_PLAN.weekly.seat;
  const yearlyPerWeekTotal = TEAM_PLAN.yearly.creditTierTotalsPerWeek[credits] + extraSeats * TEAM_PLAN.yearly.seatPerWeek;
  const savings = teamCreditTierSavings(credits, billingPeriod);

  const create = () => onCreate({ billingPeriod, seats, credits });

  // Same hoverable-bullet system as PRO/MAX (FeatureTooltip), with team tips.
  const TEAM_BENEFITS: { label: string; tip: string }[] = [
    { label: t('team.cardBenefitShared'), tip: t('team.tipShared') },
    { label: t('team.cardBenefitPooled'), tip: t('team.tipPooled') },
    { label: t('team.cardBenefitRoles'), tip: t('team.tipRoles') },
    { label: t('team.cardBenefitUsage'), tip: t('team.tipUsage') },
    { label: t('team.cardBenefitAttribution'), tip: t('team.tipAttribution') },
    { label: t('team.cardBenefitTopup'), tip: t('team.tipTopup') },
    { label: t('team.cardBenefitAllModels'), tip: t('team.tipAllModels') },
  ];

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={t('team.createYourTeam')}
      onClick={create}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); create(); } }}
      className="relative h-full rounded-2xl bg-surface flex flex-col cursor-pointer transition-all duration-200 hover:shadow-lg border border-glass-border hover:border-primary/40"
    >
      <div className="absolute -top-3 right-4 z-10">
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-primary to-accent text-white text-xs font-bold shadow-lg">
          <UsersRound className="w-3 h-3" />
          {t('team.cardBadge')}
        </span>
      </div>
      <div className="p-6 md:p-8 flex-1 flex flex-col">
        {/* min-h mirrors PlanCard so price rows + CTAs align across the grid. */}
        <div className="mb-5">
          <h3 className="text-2xl font-bold text-text-primary mb-1">TEAM</h3>
          <p className="text-sm text-text-secondary min-h-10">{t('team.cardDescription')}</p>
        </div>
        <div className="mb-5">
          <div className="flex items-baseline gap-1.5">
            <span className="text-4xl font-bold text-text-primary">${weeklyPrice}</span>
            <span className="text-text-secondary text-sm">{t('upgrade.perWeek')}</span>
          </div>
          <div className="h-5 mt-1.5">
            {billingPeriod === 'weekly' && (
              <button
                onClick={(e) => { e.stopPropagation(); onSwitchYearly(); }}
                className="inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-500 underline underline-offset-2 decoration-green-600/40 transition-colors"
              >
                <span>{t('upgrade.switchYearlySave', { amount: weeklyBaseTotal - yearlyPerWeekTotal })}</span>
                <ArrowUpRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        <p className="text-xs text-text-secondary text-center mb-2">{t('upgrade.cancelAnytime')}</p>
        {/* CTA carries the LIVE price — the configurator sits further down, so
            the feedback loop (change seats/credits → see the cost) stays right
            on the button the user will press. */}
        <button
          onClick={(e) => { e.stopPropagation(); create(); }}
          className="w-full py-3.5 mb-1 rounded-xl bg-primary hover:bg-primary/90 text-white text-base font-semibold transition-colors flex items-center justify-center gap-2"
        >
          {t('upgrade.startCreating')}
        </button>
        <p className="text-xs text-text-secondary text-center mb-2 h-4">
          {billingPeriod === 'yearly' ? t('upgrade.billedYearlyAt', { amount: yearlyTotal }) : ''}
        </p>
        {/* Mini trust strip — identical to the other plan cards */}
        <div className="mb-5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5">
          <span className="inline-flex items-center gap-1 text-[11px] text-text-tertiary">
            <Lock className="w-3 h-3" /> {t('upgrade.secureCheckout')}
          </span>
          <span className="hidden text-text-tertiary/40 sm:inline">·</span>
          <span className="flex items-center gap-1">
            {PAYMENT_LOGOS.slice(0, 4).map((logo) => (
              <img
                key={logo.name}
                src={logo.src}
                alt={logo.name}
                title={logo.name}
                loading="lazy"
                className="h-4 w-auto rounded-[3px] ring-1 ring-glass-border"
              />
            ))}
          </span>
        </div>
        {/* Trustpilot badge — identical to the other plan cards */}
        <div className="mb-5 flex items-center justify-center gap-1.5">
          <img src={TRUSTPILOT.icon} alt="Trustpilot" className="h-4 w-4 rounded-sm" />
          <span className="text-xs font-semibold text-text-secondary">Trustpilot</span>
          <span className="flex items-center gap-0.5">
            {[...Array(5)].map((_, i) => (
              <span key={i} className="flex h-3.5 w-3.5 items-center justify-center rounded-[2px] bg-[#00B67A]">
                <Star className="h-2 w-2 fill-white text-white" />
              </span>
            ))}
          </span>
          <span className="text-xs font-semibold text-text-primary">{TRUSTPILOT.rating.toFixed(1)}</span>
        </div>
        {/* Configurator — the ONLY structural difference vs PRO/MAX, placed
            below the shared sections so the CTAs align across all three cards.
            Clicks must not bubble to the card-as-CTA handler. */}
        <div
          className="mb-4 rounded-xl border border-glass-border bg-surface-light/40 divide-y divide-glass-border cursor-default"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-3.5 py-2.5">
            <div className="min-w-0">
              <p className="text-sm font-medium text-text-primary">{t('team.seatsLabel')}</p>
              <p className="text-[10px] text-text-tertiary">
                {t('team.seatPriceHint', {
                  price: billingPeriod === 'weekly' ? TEAM_PLAN.weekly.seat : TEAM_PLAN.yearly.seatPerWeek,
                })}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => setSeats((s) => Math.max(TEAM_PLAN.includedSeats, s - 1))}
                disabled={seats <= TEAM_PLAN.includedSeats}
                className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30 flex items-center justify-center transition-colors"
                aria-label={t('team.fewerSeats')}
              >
                <Minus className="w-3.5 h-3.5 text-text-secondary" />
              </button>
              <span className="w-7 text-center text-base font-bold text-text-primary tabular-nums">{seats}</span>
              <button
                onClick={() => setSeats((s) => Math.min(TEAM_PLAN.maxSeats, s + 1))}
                className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                aria-label={t('team.moreSeats')}
              >
                <Plus className="w-3.5 h-3.5 text-text-secondary" />
              </button>
            </div>
          </div>
          <div className="px-3.5 py-2.5">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-sm font-medium text-text-primary">{t('team.weeklyCreditsLabel')}</p>
              <span
                className={cn(
                  'text-[10px] font-bold px-2 py-0.5 rounded-full transition-opacity',
                  savings > 0 ? 'text-emerald-300 bg-emerald-500/10 opacity-100' : 'opacity-0'
                )}
              >
                {t('team.savePercent', { percent: savings })}
              </span>
            </div>
            <div className="grid grid-cols-5 gap-1">
              {TEAM_PLAN.creditTiers.map((tier) => (
                <button
                  key={tier}
                  onClick={() => setCredits(tier)}
                  className={cn(
                    'py-1.5 rounded-lg text-[13px] font-bold text-center border transition-all',
                    credits === tier
                      ? 'border-primary bg-primary/15 text-primary'
                      : 'border-glass-border text-text-secondary hover:border-primary/40'
                  )}
                >
                  {tier}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Bullets — exact PlanCard structure: headline + first common
            features, then an accordion with the rest + MAX features (all
            included on TEAM) + the "Only on TEAM" section, mirroring how MAX
            surfaces its differences vs PRO. */}
        <div className="space-y-2.5 flex-1 cursor-default" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start gap-2.5">
            <Check className="mt-0.5 w-5 h-5 flex-shrink-0 text-green-500" />
            <span className="text-base font-semibold text-text-primary">
              <FeatureTooltip
                feature={{ key: 'benefitCreditsVideos', tipKey: 'tipCredits' }}
                label={colorizeCredits(t('team.cardCreditsHeadline', { credits, seats }), credits)}
                tip={t('team.tipTeamCredits', { credits })}
                labelClassName="text-base font-semibold text-text-primary"
              />
            </span>
          </div>

          {primaryFeatures.map((f) => <FeatureRow key={f.key} feature={f} isMax />)}

          <div className={cn('grid transition-[grid-template-rows] duration-300 ease-out', showAllFeatures ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
            <div className="overflow-hidden min-h-0">
              <div className="space-y-2.5 pt-0.5">
                {restFeatures.map((f) => <FeatureRow key={f.key} feature={f} isMax />)}
                {/* MAX-exclusive features are all included on TEAM. */}
                {MAX_FEATURES.map((f) => <FeatureRow key={f.key} feature={f} isMax />)}
                <div className="mt-1 border-t border-glass-border pt-3">
                  <p className="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-text-tertiary">
                    {t('team.onlyOnTeam')}
                  </p>
                  <div className="space-y-2.5">
                    {TEAM_BENEFITS.map((b) => (
                      <div key={b.label} className="flex items-start gap-2.5">
                        <Check className="mt-0.5 w-4 h-4 flex-shrink-0 text-green-500" />
                        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-text-primary">
                          <FeatureTooltip
                            feature={{ key: 'teamBenefit', tipKey: 'teamBenefit' }}
                            label={b.label}
                            tip={b.tip}
                          />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); setShowAllFeatures((v) => !v); }}
            className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            {showAllFeatures ? t('upgrade.showFewerFeatures') : t('upgrade.showAllFeatures')}
            <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', showAllFeatures && 'rotate-180')} />
          </button>
        </div>
      </div>
    </div>
  );
}

// A 5-star bar with the gold fill clipped to the aggregate rating (e.g. 4.8 →
// 96% filled), drawn over a faint empty row of the same stars.
function StarBar({ rating, size = 14 }: { rating: number; size?: number }) {
  const pct = Math.max(0, Math.min(100, (rating / 5) * 100));
  const stars = (cls: string) => (
    <span className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className={cn('shrink-0 fill-current', cls)} style={{ width: size, height: size }} />
      ))}
    </span>
  );
  return (
    <span className="relative inline-flex" aria-label={`${rating} / 5`}>
      {stars('text-text-tertiary/30')}
      <span className="absolute inset-0 overflow-hidden" style={{ width: `${pct}%` }}>
        {stars('text-amber-400')}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// ReviewsSection — switchable, brand-colored social proof. One tab per platform
// (logo + partially-filled star rating), a big brand-tinted quote, the review
// title/body, the author, a 5-star rating, prev/next across that platform's
// reviews, and a "Go to Review" link to the live page.
// ---------------------------------------------------------------------------
export function ReviewsSection() {
  const { t } = useTranslation('v2');
  const [activeId, setActiveId] = useState(REVIEW_PLATFORMS[0].id);
  const [idx, setIdx] = useState(0);
  const platform = REVIEW_PLATFORMS.find((p) => p.id === activeId) ?? REVIEW_PLATFORMS[0];
  const reviews = platform.reviews;
  const review = reviews[Math.min(idx, reviews.length - 1)];

  const select = (id: string) => { setActiveId(id); setIdx(0); };
  const prev = () => setIdx((i) => (i - 1 + reviews.length) % reviews.length);
  const next = () => setIdx((i) => (i + 1) % reviews.length);

  return (
    <div id="reviews" className="mb-10 scroll-mt-24">
      <h2 className="mb-5 text-center text-xl font-bold text-text-primary">{t('upgrade.whatCreatorsSay')}</h2>

      <div className="overflow-hidden rounded-2xl border border-glass-border bg-surface">
        {/* Platform tabs */}
        <div className="flex overflow-x-auto border-b border-glass-border">
          {REVIEW_PLATFORMS.map((p) => {
            const active = activeId === p.id;
            return (
              <button
                key={p.id}
                onClick={() => select(p.id)}
                style={active ? { borderColor: p.color, backgroundColor: `${p.color}14` } : undefined}
                className={cn(
                  'flex shrink-0 items-center gap-2.5 whitespace-nowrap border-b-2 px-4 py-3.5 transition-colors sm:px-5',
                  !active && 'border-transparent hover:bg-surface-light/40',
                )}
              >
                <img src={p.icon} alt="" className="h-6 w-6 rounded" />
                <span className="text-left">
                  <span className="block text-sm font-bold text-text-primary">{p.name}</span>
                  <span className="mt-0.5 flex items-center gap-1.5">
                    <StarBar rating={p.rating} size={12} />
                    <span className="text-[11px] font-semibold text-text-secondary">{p.rating.toFixed(1)}</span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Active review */}
        <div
          key={`${activeId}-${idx}`}
          style={{ borderTopColor: platform.color }}
          className="grid animate-fade-in grid-cols-1 gap-5 border-t-2 p-6 md:grid-cols-[210px_1fr] md:gap-8 md:p-8"
        >
          <div className="flex items-center justify-between md:flex-col md:items-start">
            <h3 className="text-2xl font-extrabold leading-tight text-text-primary">{t('upgrade.platformReviews', { platform: platform.name })}</h3>
            {reviews.length > 1 && (
              <div className="flex gap-2 md:mt-auto md:pt-6">
                <button onClick={prev} aria-label={t('upgrade.prevReview')} className="flex h-9 w-9 items-center justify-center rounded-full border border-glass-border text-text-secondary transition-colors hover:border-primary/50 hover:text-primary">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button onClick={next} aria-label={t('upgrade.nextReview')} className="flex h-9 w-9 items-center justify-center rounded-full border border-glass-border text-text-secondary transition-colors hover:border-primary/50 hover:text-primary">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          <div>
            <div className="mb-3 flex items-start justify-between gap-4">
              <Quote className="h-9 w-9 shrink-0" style={{ color: platform.color, fill: platform.color }} />
              <a
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-glass-border px-3 py-1.5 text-xs font-semibold text-text-primary transition-colors hover:border-primary/50 hover:text-primary"
              >
                {t('upgrade.goToReview')} <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
            <h4 className="mb-2 text-lg font-bold text-text-primary">{t(`upgrade.${review.titleKey}`)}</h4>
            <p className="text-sm leading-relaxed text-text-secondary md:text-[15px]">{t(`upgrade.${review.textKey}`)}</p>
            <div className="mt-5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <img
                  src={review.avatar}
                  alt={review.author}
                  loading="lazy"
                  className="h-10 w-10 rounded-full object-cover ring-2"
                  style={{ ['--tw-ring-color' as string]: `${platform.color}66` }}
                />
                <div>
                  <p className="text-sm font-semibold text-text-primary">{review.author}</p>
                  <p className="text-xs text-text-tertiary">{t('upgrade.verifiedReview')}</p>
                </div>
              </div>
              <StarBar rating={5} size={16} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PricingCarousel — mobile-only (< md) horizontally swipeable plan deck for the
// A/B cohort (abGroup 0–7). Design goal: make it UNMISTAKABLE that there are
// other plans to the side. It stacks every discoverability cue at once:
//   • Peek — slides are 86% wide with 7% side padding, so both neighbors bleed
//     in at the edges (the single strongest "there's more sideways" signal).
//   • Snap — scroll-snap centers each card as you swipe.
//   • Nudge — a one-time auto wiggle on mount that reveals + springs back.
//   • Dots — a tappable indicator row (active pill widened) below the deck.
//   • Arrows — edge chevrons (disabled/faded at the ends) for tap navigation.
//   • Hint — an animated "Swipe to compare plans" line that fades on first scroll.
// Defaults centered on `initialIndex` (MAX). Desktop keeps the classic grid.
// ---------------------------------------------------------------------------
function PricingCarousel({ slides, initialIndex }: { slides: React.ReactNode[]; initialIndex: number }) {
  const { t } = useTranslation('v2');
  const scrollerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [active, setActive] = useState(initialIndex);
  // Once the user actually scrolls/swipes, they've "got it" → drop the hint.
  const [hinted, setHinted] = useState(false);
  const rafRef = useRef<number | null>(null);

  const centerOn = (i: number, behavior: ScrollBehavior) => {
    const scroller = scrollerRef.current;
    const el = itemRefs.current[i];
    if (!scroller || !el) return;
    const left = el.offsetLeft - (scroller.clientWidth - el.offsetWidth) / 2;
    scroller.scrollTo({ left, behavior });
  };

  // Center on the default plan on mount (and on resize), then run the one-time
  // nudge: shove sideways a touch to expose the hidden card, then spring back.
  useEffect(() => {
    centerOn(initialIndex, 'auto');
    const nudge = setTimeout(() => {
      const scroller = scrollerRef.current;
      if (!scroller) return;
      scroller.scrollBy({ left: 52, behavior: 'smooth' });
      setTimeout(() => scroller.scrollBy({ left: -52, behavior: 'smooth' }), 340);
    }, 550);
    const onResize = () => centerOn(active, 'auto');
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(nudge);
      window.removeEventListener('resize', onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track which card is centered (rAF-throttled) → drives the dots + arrow bounds.
  const onScroll = () => {
    if (!hinted) setHinted(true);
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const scroller = scrollerRef.current;
      if (!scroller) return;
      const center = scroller.scrollLeft + scroller.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      itemRefs.current.forEach((el, i) => {
        if (!el) return;
        const c = el.offsetLeft + el.offsetWidth / 2;
        const d = Math.abs(c - center);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      setActive(best);
    });
  };

  const arrowCls =
    'absolute top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-glass-border bg-surface/90 text-text-primary shadow-lg backdrop-blur transition-opacity disabled:pointer-events-none disabled:opacity-0';

  return (
    <div className="relative md:hidden -mx-4 sm:-mx-6 mb-8">
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto px-[7%] pt-4 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {slides.map((slide, i) => (
          <div
            key={i}
            ref={(el) => { itemRefs.current[i] = el; }}
            className="min-w-0 shrink-0 basis-[86%] snap-center"
          >
            {slide}
          </div>
        ))}
      </div>

      {/* Edge arrows — fade out (and stop capturing taps) at the two ends. */}
      <button
        type="button"
        aria-label={t('upgrade.prevPlan')}
        onClick={() => centerOn(Math.max(0, active - 1), 'smooth')}
        disabled={active === 0}
        className={cn(arrowCls, 'left-1')}
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label={t('upgrade.nextPlan')}
        onClick={() => centerOn(Math.min(slides.length - 1, active + 1), 'smooth')}
        disabled={active === slides.length - 1}
        className={cn(arrowCls, 'right-1')}
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* Pagination dots — active one widens into a pill. */}
      <div className="mt-3 flex items-center justify-center gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`${i + 1} / ${slides.length}`}
            aria-current={i === active}
            onClick={() => centerOn(i, 'smooth')}
            className={cn(
              'h-2 rounded-full transition-all duration-300',
              i === active ? 'w-6 bg-primary' : 'w-2 bg-glass-border hover:bg-text-tertiary',
            )}
          />
        ))}
      </div>

      {/* Swipe hint — animated, fades away the moment the deck is scrolled. */}
      <div
        className={cn(
          'mt-2 flex items-center justify-center gap-1.5 text-xs text-text-tertiary transition-opacity duration-500',
          hinted ? 'opacity-0' : 'opacity-100',
        )}
        aria-hidden={hinted}
      >
        <MoveHorizontal className="h-3.5 w-3.5 animate-pulse text-primary" />
        {t('upgrade.swipeToCompare')}
      </div>
    </div>
  );
}

export function UpgradePage() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const userId = useUserStore((s) => s.userId);
  const refreshUser = useUserStore((s) => s.refreshUser);
  const isPaid = isPaidPlan(user?.userPlan);
  // A/B (abGroup 0–7): swipeable plan carousel on mobile instead of stacked
  // cards. Desktop layout is identical for every group.
  const swipeablePricing = usesSwipeablePricingCards(user?.abGroup);
  // Conversion funnel: the project the user was trying to generate when the
  // paywall appeared. We carry it through Stripe (success_url) so we can drop
  // them right back on it after payment, where auto-resume kicks off the render.
  const [fromOrder] = useState(() =>
    new URLSearchParams(window.location.search).get('from_order'));
  // Manual-mode conversion funnel: no order exists yet, so we just bring them
  // back to Home where the stashed form auto-resumes (see MANUAL_STASH_KEY).
  const [fromManual] = useState(() =>
    new URLSearchParams(window.location.search).get('from_manual'));

  // V1 behavior: weekly subscribers land on the weekly toggle. Recovery-email
  // visitors (?from=email) also default to weekly — that's the plan the old email
  // link opened, and it avoids the $99 yearly price shock that tanks completion.
  const [fromEmail] = useState(() =>
    new URLSearchParams(window.location.search).get('from') === 'email');
  // Recovery-email offer id (&offer=email_699|email_499|email_199), kept for the tab session
  // like the attribution; not read on the ?success=true return (cleared there).
  const [emailOfferId] = useState<EmailOfferId | null>(() => {
    if (new URLSearchParams(window.location.search).get('success') === 'true') return null;
    return captureEmailOffer(window.location.search) || getEmailOffer();
  });
  const [billingPeriod, setBillingPeriod] = useState<'weekly' | 'yearly'>(() =>
    fromEmail || emailOfferId || (isPaidPlan(user?.userPlan) && user?.priceType === 'weekly') ? 'weekly' : 'yearly');
  // Conversion-funnel / paywall context (?context=…) — V1 context set. Email CTAs
  // arrive with ?tpl=<template_id>, tagged as email_<tpl> so recovery visits are
  // distinguishable in the paywall-impression funnel.
  const [funnelContext] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const ctx = params.get('context');
    if (ctx) return ctx;
    if (params.get('from') === 'email') return `email_${params.get('tpl') || 'unknown'}`;
    return null;
  });
  // Recovery-email identity (seq/step/var/cmp/tpl): a new email link is stored for
  // the tab session; otherwise the copy stored earlier (e.g. before login) is reused.
  const [emailAttribution] = useState(() =>
    captureEmailAttribution(window.location.search) || getEmailAttribution());
  // Live email offer from v2-email-offer status (null until confirmed active).
  const [emailOffer, setEmailOffer] = useState<{ offer: EmailOfferId; priceCents: number; expiresAt: number } | null>(null);
  const [emailOfferExpired, setEmailOfferExpired] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<'pro' | 'max' | null>(null);
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);
  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  // Config picked on the Team card — the modal then only asks for the name.
  const [teamConfig, setTeamConfig] = useState<{ billingPeriod: 'weekly' | 'yearly'; seats: number; credits: number } | null>(null);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);
  const [canceledBanner, setCanceledBanner] = useState(false);
  // Credit top-up (paid users) — V1 rates: $2 per 10 credits.
  const [creditAmount, setCreditAmount] = useState(DEFAULT_CREDITS);
  const topUpPrice = (creditAmount / CREDITS_PER_PACK) * PRICE_PER_PACK;
  // Same ConfirmPaymentModal as V1 (card on file + prorated yearly preview).
  const { openPayment, paymentModal } = useDirectPayment();

  // Pricing-page paywall impression — once per mount, for ANY arrival (nav
  // clicks land here with no ?context= → 'direct'), EXCEPT Stripe round-trip
  // returns (success/canceled), which are checkout outcomes, not encounters.
  // Mirrors V1's SubscriptionPage exactly so the arms stay comparable.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true' || params.get('canceled') === 'true') return;
    const emailProps = emailAttributionEventProps(emailAttribution);
    trackPaywallShown(
      funnelContext || 'direct',
      emailOfferId ? { ...emailProps, email_offer: emailOfferId } : emailProps,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recovery-email offer: the server owns price + deadline; show it only while active.
  // Held from mount so leaving before the status lands does not start the exit offer.
  const emailOfferHoldRef = useRef(!!emailOfferId);
  const emailOfferViewedRef = useRef(false);
  const expireEmailOffer = useCallback(() => {
    emailOfferHoldRef.current = false;
    setEmailOffer(null);
    setEmailOfferExpired(true);
  }, []);
  useEffect(() => {
    if (!emailOfferId || !userId || isPaid) return;
    let cancelled = false;
    void (async () => {
      try {
        const status = await apiService.emailOfferStatus(userId);
        if (cancelled) return;
        if (!status.active || status.expiresAt <= Date.now()) {
          expireEmailOffer();
          return;
        }
        const offer = status.offer || emailOfferId;
        const priceCents = status.priceCents || EMAIL_OFFER_FALLBACK_CENTS[offer];
        emailOfferHoldRef.current = true;
        setEmailOffer({ offer, priceCents, expiresAt: status.expiresAt });
        setEmailOfferExpired(false);
        if (!emailOfferViewedRef.current) {
          emailOfferViewedRef.current = true;
          trackEmailOfferViewed(offer, priceCents, new Date(status.expiresAt).toISOString());
        }
      } catch {
        // status unknown → normal price, no note
        if (!cancelled) emailOfferHoldRef.current = false;
      }
    })();
    return () => { cancelled = true; };
  }, [emailOfferId, userId, isPaid, expireEmailOffer]);

  // Recovery/exit offer trigger: when a FREE user LEAVES this page without ever
  // clicking a checkout button, ask the server to start the one-time $4.99 offer.
  // The server enforces eligibility (free + never started a checkout) and owns the
  // deadline, so this is just the "leave" signal. We skip mounts that are Stripe
  // returns and any visit where the user did engage a checkout.
  const requestExitOffer = useExitOfferStore((s) => s.requestExitOffer);
  const checkoutClickedRef = useRef(false);
  const isStripeReturnRef = useRef(
    (() => {
      const p = new URLSearchParams(window.location.search);
      return p.get('success') === 'true' || p.get('canceled') === 'true'
        || p.get('update_card') === 'true';
    })()
  );
  // Latest identity/plan snapshot for the unmount handler (refs avoid stale closures).
  const leaveSnapRef = useRef({ userId, isPaid });
  leaveSnapRef.current = { userId, isPaid };
  useEffect(() => {
    return () => {
      const { userId: uid, isPaid: paid } = leaveSnapRef.current;
      if (checkoutClickedRef.current || isStripeReturnRef.current) return;
      if (emailOfferHoldRef.current) return;  // an email offer is on the page — no exit offer over it
      if (paid || !uid) return;
      void requestExitOffer(uid);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stripe return (?success=true) — V1 parity: dedupe by txn_id, fire the
  // purchase event once, refresh credits/plan, show success.
  // Also handles ?update_card=true after "Use another card" setup Checkout:
  // adopt the new PM, then reopen the yearly-switch confirm modal.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('canceled') === 'true') {
      setCanceledBanner(true);
      trackCheckoutCanceled(emailAttributionEventProps(getEmailAttribution()));
      window.history.replaceState({}, '', '/subscription');
      return;
    }

    // Return from setup-mode "update card" Checkout — adopt PM, then retry yearly.
    // Webhook may already have adopted the card; treat adopt failure as soft so we
    // still reopen the confirm modal with the (likely already-updated) card.
    if (params.get('update_card') === 'true') {
      const sessionId = params.get('session_id');
      const priceId = params.get('price_id');
      window.history.replaceState({}, '', '/subscription');
      if (!userId || !sessionId) return;
      void (async () => {
        let adopted = false;
        try {
          await apiService.adoptPaymentMethod(userId, sessionId);
          adopted = true;
        } catch (err) {
          console.warn('adoptPaymentMethod failed (webhook may have already adopted):', err);
        }
        try {
          await refreshUser();
          if (adopted) {
            toast.success(t('upgrade.cardUpdatedToast'));
          }
          if (priceId) {
            const cfg = user?.userPlan === 'max' ? MAX_PLAN : PRO_PLAN;
            openPayment({
              type: 'upgrade',
              label: t('upgrade.switchYearlySave', { amount: cfg.weeklyAmount - cfg.yearlyPerWeek }),
              description: `$${cfg.yearlyPerWeek}${t('upgrade.perWeek')}`,
              amount: `$${cfg.yearlyAmount}/${t('upgrade.yearly').toLowerCase()}`,
              priceId,
            });
          }
        } catch {
          toast.error(t('upgrade.cardUpdateError'));
        }
      })();
      return;
    }

    if (params.get('success') !== 'true') return;
    // Purchase done → stop attributing later (organic) checkouts to the old email.
    clearEmailAttribution();
    const txnId = params.get('txn_id');
    const planKey = (params.get('plan') as 'pro' | 'max') || 'pro';
    const period = (params.get('period') as 'weekly' | 'yearly') || 'yearly';
    const credits = params.get('credits');
    if (credits) clearDirectPaymentFailureFlag();
    const dedupeKey = txnId ? `vivideo_tracked_${txnId}` : null;
    if (txnId && dedupeKey && !sessionStorage.getItem(dedupeKey)) {
      sessionStorage.setItem(dedupeKey, 'true');
      if (credits) {
        const n = parseInt(credits, 10);
        trackCreditsPurchase(n, (n / CREDITS_PER_PACK) * PRICE_PER_PACK, txnId);
      } else {
        const cfg = planKey === 'max' ? MAX_PLAN : PRO_PLAN;
        trackPurchase({
          planType: `${planKey}_${period}`,
          value: period === 'yearly' ? cfg.yearlyAmount : cfg.weeklyAmount,
          isSubscription: true,
          transactionId: txnId,
        });
      }
    }
    setSuccessBanner(credits ? t('upgrade.creditsAdded', { count: parseInt(credits, 10) }) : t('upgrade.paymentSuccessful'));
    // The plan flip is written by the STRIPE WEBHOOK, which races our redirect
    // (typically 1-3s behind). A single refreshUser() can fetch a still-Free
    // user; the project's auto-resume would then never fire and a PAYING user
    // would land back on the blurred gate. Poll briefly until the webhook has
    // landed; bail after ~12s and navigate anyway (a later refresh recovers).
    const waitForUpgrade = async () => {
      for (let i = 0; i < 8; i++) {
        await refreshUser();
        if (isPaidPlan(useUserStore.getState().user?.userPlan)) return;
        await new Promise((r) => setTimeout(r, 1500));
      }
    };
    // Came from the "generate this video" funnel → wait for the (now Pro) plan,
    // then drop them straight back on that project, where the auto-resume effect
    // starts the real render. We wait FIRST so isProUser is true on arrival.
    const returnOrder = params.get('from_order');
    if (returnOrder && !credits) {
      void (async () => {
        await waitForUpgrade();
        navigate(`/projects/${returnOrder}`, { replace: true });
      })();
      return;
    }
    // Manual-mode funnel: no order to open — go Home, wait FIRST so isPro() is
    // true, and the stashed form there auto-resumes the render.
    const returnManual = params.get('from_manual');
    if (returnManual && !credits) {
      void (async () => {
        await waitForUpgrade();
        navigate('/', { replace: true });
      })();
      return;
    }
    void refreshUser();
    window.history.replaceState({}, '', '/subscription');
    // openPayment / user used by the update_card branch above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshUser, navigate, userId, t]);

  const toggleBilling = (period: 'weekly' | 'yearly') => {
    if (period === billingPeriod) return;
    setBillingPeriod(period);
    trackBillingPeriodToggle(period, 'none');
  };

  // Same checkout flow as the live V1 SubscriptionPage (redirect to Stripe).
  const handleSelect = async (planKey: 'pro' | 'max') => {
    if (!userId || loadingPlan) return;
    checkoutClickedRef.current = true;  // engaged a checkout → no exit offer on leave
    setLoadingPlan(planKey);
    // The email offer applies to Pro weekly only; the server re-validates its deadline.
    const offer = planKey === 'pro' && billingPeriod === 'weekly' ? emailOffer : null;
    try {
      const priceId = PLAN_PRICE_IDS[planKey][billingPeriod];
      const cfg = planKey === 'max' ? MAX_PLAN : PRO_PLAN;
      if (offer) trackBeginCheckout(`pro_weekly_${offer.offer}`, offer.priceCents / 100);
      else trackBeginCheckout(`${planKey}_${billingPeriod}`, billingPeriod === 'yearly' ? cfg.yearlyAmount : cfg.weeklyAmount);
      const txnId = newTxnId();
      const { checkoutUrl } = await apiService.createCheckoutSession(
        userId,
        billingPeriod,
        user?.email || undefined,
        undefined,
        `${window.location.origin}/subscription?success=true&txn_id=${txnId}&plan=${planKey}&period=${billingPeriod}${fromOrder ? `&from_order=${fromOrder}` : ''}${fromManual ? `&from_manual=${fromManual}` : ''}`,
        `${window.location.origin}/subscription?canceled=true&plan=${planKey}`,
        false,
        priceId,
        undefined,
        undefined,
        undefined,
        undefined,
        offer?.offer,
        getEmailAttribution()
      );
      if (checkoutUrl) window.location.href = checkoutUrl;
      else toast.error(t('upgrade.checkoutError'));
    } catch (err) {
      // 410: the offer deadline passed → normal price + "offer expired" note.
      if (offer && err instanceof ExitOfferExpiredError) expireEmailOffer();
      else toast.error(t('upgrade.checkoutError'));
    } finally {
      setLoadingPlan(null);
    }
  };

  // One-time credit top-up — opens V1's ConfirmPaymentModal (saved card).
  const setCredits = (v: number) =>
    setCreditAmount(Math.round(Math.max(MIN_CREDITS, Math.min(MAX_CREDITS, v)) / CREDIT_STEP) * CREDIT_STEP);

  const handleBuyCredits = () => {
    if (!userId) return;
    checkoutClickedRef.current = true;
    trackBeginCheckout('credits', topUpPrice);
    openPayment({
      type: 'credits',
      label: t('upgrade.buyAdditionalCredits'),
      description: `${creditAmount} ${t('upgrade.creditsLabel')}`,
      amount: `$${topUpPrice.toFixed(2)}`,
      creditAmount,
    });
  };

  // Weekly→yearly switch — same ConfirmPaymentModal as V1 (prorated preview inside).
  const handleSwitchYearlyDirect = () => {
    if (!userId || !isPaid) return;
    const cfg = user?.userPlan === 'max' ? MAX_PLAN : PRO_PLAN;
    openPayment({
      type: 'upgrade',
      label: t('upgrade.switchYearlySave', { amount: cfg.weeklyAmount - cfg.yearlyPerWeek }),
      description: `$${cfg.yearlyPerWeek}${t('upgrade.perWeek')}`,
      amount: `$${cfg.yearlyAmount}/${t('upgrade.yearly').toLowerCase()}`,
      priceId: cfg.yearlyPriceId,
    });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto animate-fade-in pb-8">
        <div className="pb-12 pt-8 px-0">
          {funnelContext === 'video_ready' && (
            <div className="mb-6 rounded-2xl border border-primary/40 bg-gradient-to-r from-primary/15 to-accent/10 p-4 text-center animate-slide-up">
              <p className="text-base font-bold text-text-primary">{t('upgrade.videoReadyTitle')}</p>
              <p className="text-sm text-text-secondary mt-1">{t('upgrade.videoReadyDesc')}</p>
            </div>
          )}
          {funnelContext === 'insufficient_credits' && (
            <div className="mb-6 rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4 text-center animate-slide-up">
              <p className="text-base font-bold text-text-primary">{t('upgrade.outOfCreditsTitle')}</p>
              <p className="text-sm text-text-secondary mt-1">
                {isPaid ? t('upgrade.outOfCreditsPaid') : t('upgrade.outOfCreditsFree')}
              </p>
            </div>
          )}
          {funnelContext === 'upgrade_for_full_video' && (
            <div className="mb-6 rounded-2xl border border-primary/40 bg-gradient-to-r from-primary/15 to-accent/10 p-4 text-center animate-slide-up">
              <p className="text-base font-bold text-text-primary">{t('upgrade.fullVideoTitle')}</p>
              <p className="text-sm text-text-secondary mt-1">{t('upgrade.fullVideoDesc')}</p>
            </div>
          )}
          {(funnelContext === 'pro_feature' || funnelContext === 'download' || funnelContext === 'share') && (
            <div className="mb-6 rounded-2xl border border-primary/40 bg-primary/10 p-4 text-center animate-slide-up">
              <p className="text-base font-bold text-text-primary">
                {funnelContext === 'download' ? t('upgrade.downloadTitle')
                  : funnelContext === 'share' ? t('upgrade.shareTitle')
                  : t('upgrade.proFeatureTitle')}
              </p>
              <p className="text-sm text-text-secondary mt-1">{t('upgrade.unlockAnyPlan')}</p>
            </div>
          )}
          {successBanner && (
            <div className="mb-6 rounded-2xl border border-primary/40 bg-primary/10 p-4 text-center animate-slide-up">
              <p className="text-base font-bold text-text-primary">{successBanner}</p>
            </div>
          )}
          {canceledBanner && (
            <div className="mb-6 rounded-2xl border border-glass-border bg-surface-light/50 p-4 text-center animate-slide-up">
              <p className="text-base font-bold text-text-primary">{t('upgrade.checkoutCanceled')}</p>
              <p className="text-sm text-text-secondary mt-1">{t('upgrade.checkoutCanceledDesc')}</p>
            </div>
          )}
          <div className="text-center mb-8">
            <h1 className={cn(
              'font-bold text-text-primary mb-2',
              // "Choose your plan" is a plain label → smaller; the video/credits
              // headlines still carry weight so keep those larger.
              isPaid || funnelContext === 'video_ready' || funnelContext === 'upgrade_for_full_video'
                ? 'text-3xl' : 'text-lg sm:text-xl',
            )}>
              {isPaid ? t('upgrade.buyAdditionalCredits')
                : (funnelContext === 'video_ready' || funnelContext === 'upgrade_for_full_video') ? t('upgrade.unlockYourVideo')
                : t('upgrade.chooseYourPlan')}
            </h1>
            {/* choosePlanSubtitle ("Unlock unlimited …") intentionally removed for
                free users (owner, 2026-07-08); the paid top-up subtitle stays.
                The Trustpilot badge under the heading was also removed (owner) —
                the per-card Trustpilot badge + the reviews section still carry it. */}
            {isPaid && (
              <p className="text-text-secondary">
                {t('upgrade.topUpSubtitle', { price: PRICE_PER_PACK, credits: CREDITS_PER_PACK })}
              </p>
            )}
          </div>

          {/* Free users: social proof with a live-ticking signup counter. */}
          {!isPaid && (
            <p className="mb-8 -mt-5 flex items-center justify-center gap-1.5 text-sm text-text-secondary">
              <Users className="w-4 h-4 text-primary" /> <LiveSignupCounter />
            </p>
          )}

          {isPaid ? (
            /* ===== PAID USERS — credit top-up (V1 rates) + yearly push ===== */
            <div className="mb-10">
              {user?.priceType === 'weekly' && (
                <button
                  onClick={handleSwitchYearlyDirect}
                  className="w-full mb-6 p-5 rounded-2xl border border-green-700/30 bg-gradient-to-br from-green-700/10 to-green-600/5 hover:border-green-600/50 transition-all text-left flex items-center gap-4"
                >
                  <span className="w-11 h-11 rounded-xl bg-green-700/20 flex items-center justify-center shrink-0">
                    <ArrowUpRight className="w-6 h-6 text-green-500" />
                  </span>
                  <span className="flex-1">
                    <span className="block text-sm font-bold text-green-600">
                      {t('upgrade.switchYearlySave', { amount: (user?.userPlan === 'max' ? MAX_PLAN : PRO_PLAN).weeklyAmount - (user?.userPlan === 'max' ? MAX_PLAN : PRO_PLAN).yearlyPerWeek })}
                    </span>
                    <span className="block text-xs text-text-secondary mt-0.5">
                      {t('upgrade.yearlyPerWeekBilled', { perWeek: (user?.userPlan === 'max' ? MAX_PLAN : PRO_PLAN).yearlyPerWeek, yearlyAmount: (user?.userPlan === 'max' ? MAX_PLAN : PRO_PLAN).yearlyAmount })}
                    </span>
                  </span>
                </button>
              )}

              <div className="p-6 rounded-2xl bg-surface border border-glass-border mb-4">
                <div className="flex justify-center mb-6">
                  <div className="inline-flex items-center gap-6 px-8 py-5 rounded-2xl bg-surface-light border border-primary/30">
                    <button
                      onClick={() => setCredits(creditAmount - CREDIT_STEP)}
                      disabled={creditAmount <= MIN_CREDITS}
                      className="w-12 h-12 rounded-full bg-surface border border-glass-border flex items-center justify-center hover:bg-surface-light disabled:opacity-50 transition-colors text-xl font-bold text-text-secondary"
                    >
                      −
                    </button>
                    <div className="min-w-[140px] text-center">
                      <input
                        type="number"
                        value={creditAmount}
                        min={MIN_CREDITS}
                        max={MAX_CREDITS}
                        step={CREDIT_STEP}
                        onChange={(e) => setCredits(parseInt(e.target.value, 10) || MIN_CREDITS)}
                        className="w-28 text-4xl font-bold text-text-primary bg-transparent text-center border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <p className="text-sm text-text-secondary mt-1">{t('upgrade.creditsLabel')}</p>
                    </div>
                    <button
                      onClick={() => setCredits(creditAmount + CREDIT_STEP)}
                      disabled={creditAmount >= MAX_CREDITS}
                      className="w-12 h-12 rounded-full bg-surface border border-glass-border flex items-center justify-center hover:bg-surface-light disabled:opacity-50 transition-colors text-xl font-bold text-text-secondary"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="text-center p-4 rounded-xl bg-primary/10 border border-primary/30">
                  <p className="text-text-secondary text-sm mb-1">{t('upgrade.totalPrice')}</p>
                  <p className="text-3xl font-bold text-primary">${topUpPrice.toFixed(2)}</p>
                  <p className="text-xs text-text-tertiary mt-1">{t('upgrade.pricePerCredits', { price: PRICE_PER_PACK, credits: CREDITS_PER_PACK })}</p>
                </div>
              </div>
              <button
                onClick={handleBuyCredits}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-base font-semibold hover:opacity-95 hover:shadow-glow transition-all flex items-center justify-center gap-2 mb-4"
              >
                <CreditCard className="w-5 h-5" />
                {t('upgrade.buyCreditsButton', { price: topUpPrice.toFixed(2) })}
              </button>

              {/* Never run dry: automatic top-up from the default card. */}
              <AutoReloadCard />
            </div>
          ) : (
            <>
              {/* Billing toggle — the pill stays perfectly centered; the
                  discount floats just to its right (absolute) so it sits NEXT
                  TO the yearly button without nudging the toggle off-center.
                  V2 layout is LTR for every language, so left-full is safe. */}
              <div className="flex justify-center mb-4">
                <div className="relative inline-flex p-1 rounded-xl bg-surface border border-glass-border">
                  <button
                    onClick={() => toggleBilling('weekly')}
                    className={cn(
                      'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                      billingPeriod === 'weekly'
                        ? 'bg-surface-light text-text-primary shadow-sm'
                        : 'text-text-tertiary hover:text-text-secondary'
                    )}
                  >
                    {t('upgrade.weekly')}
                  </button>
                  <button
                    onClick={() => toggleBilling('yearly')}
                    className={cn(
                      'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                      billingPeriod === 'yearly'
                        ? 'bg-surface-light text-text-primary shadow-sm'
                        : 'text-text-tertiary hover:text-text-secondary'
                    )}
                  >
                    {t('upgrade.yearly')}
                  </button>
                  <span className="absolute left-full top-1/2 ml-2 -translate-y-1/2 px-2.5 py-1 text-[11px] font-bold rounded-full bg-green-700 text-white whitespace-nowrap">
                    {t('upgrade.percentOff', { percent: PRO_PLAN.yearlySavingsPercent })}
                  </span>
                </div>
              </div>

              {emailOfferExpired && (
                <p className="text-center text-xs text-text-secondary mb-4">{t('exitOffer.expired')}</p>
              )}

              {/* Plan cards — all three side by side from xl up (the sidebar
                  defaults closed on this page, so there's room); the grid
                  breaks out of the max-w-4xl column so no card gets squeezed.
                  Below xl: PRO/MAX pair up and Team spans the row under them.
                  For the A/B cohort (abGroup 0–7) the MOBILE view swaps the
                  stack for a swipeable carousel; desktop is unchanged. The card
                  elements are built once and reused across both layouts. */}
              {(() => {
                const proCard = (
                  <PlanCard
                    name="PRO"
                    description={t('upgrade.proDescription')}
                    plan={PRO_PLAN}
                    videoCount={10}
                    billingPeriod={billingPeriod}
                    isMax={false}
                    onSwitchYearly={() => toggleBilling('yearly')}
                    onSelect={() => void handleSelect('pro')}
                    loading={loadingPlan === 'pro'}
                    offer={emailOffer ? {
                      price: (emailOffer.priceCents / 100).toFixed(2),
                      expiresAt: emailOffer.expiresAt,
                      onExpire: expireEmailOffer,
                    } : undefined}
                  />
                );
                const maxCard = (
                  <PlanCard
                    name="MAX"
                    description={t('upgrade.maxDescription')}
                    plan={MAX_PLAN}
                    videoCount={33}
                    billingPeriod={billingPeriod}
                    popular
                    isMax
                    onSwitchYearly={() => toggleBilling('yearly')}
                    onSelect={() => void handleSelect('max')}
                    loading={loadingPlan === 'max'}
                  />
                );
                // Team workspace plan — same card language, team-specific
                // controls (seats + shared credit pool) inline.
                const teamCard = (
                  <TeamPlanCard
                    billingPeriod={billingPeriod}
                    onSwitchYearly={() => toggleBilling('yearly')}
                    onCreate={(config) => { checkoutClickedRef.current = true; setTeamConfig(config); setCreateTeamOpen(true); }}
                  />
                );

                if (swipeablePricing) {
                  return (
                    <>
                      {/* Mobile (< md): swipeable deck centered on MAX. */}
                      <PricingCarousel slides={[proCard, maxCard, teamCard]} initialIndex={emailOfferId ? 0 : 1} />
                      {/* Desktop (≥ md): classic grid, unchanged. */}
                      <div className="hidden md:grid md:grid-cols-2 xl:grid-cols-3 gap-5 mb-10 xl:-mx-24 2xl:-mx-40">
                        {proCard}
                        {maxCard}
                        <div className="md:col-span-2 xl:col-span-1">{teamCard}</div>
                      </div>
                    </>
                  );
                }

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-10 xl:-mx-24 2xl:-mx-40">
                    {proCard}
                    {maxCard}
                    <div className="md:col-span-2 xl:col-span-1">{teamCard}</div>
                  </div>
                );
              })()}
            </>
          )}

          {/* Secure payment + accepted cards */}
          <div className="mb-6 rounded-2xl border border-glass-border bg-surface p-5">
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full border border-green-500/30 bg-green-500/10 flex-shrink-0">
                  <Lock className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-text-primary">{t('upgrade.securePayment')}</p>
                  <p className="text-xs text-text-tertiary">{t('upgrade.sslEncrypted')}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {PAYMENT_LOGOS.map((logo) => (
                  <img
                    key={logo.name}
                    src={logo.src}
                    alt={logo.name}
                    title={logo.name}
                    loading="lazy"
                    className="h-8 w-auto rounded-md ring-1 ring-glass-border"
                  />
                ))}
              </div>
            </div>
            <div className="my-4 border-t border-glass-border" />
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-text-tertiary">
              <span className="inline-flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5 text-text-secondary" /> {t('upgrade.poweredByStripe')}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-green-500" /> {t('upgrade.cancelAnytime')}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CreditCard className="w-3.5 h-3.5 text-text-secondary" /> {t('upgrade.noHiddenFees')}
              </span>
            </div>
          </div>

          {/* Enterprise banner — compact single row. Hidden on the paid-user
              credit top-up screen. Temporarily hidden for free users too
              (SHOW_ENTERPRISE_BANNER = false); flip back on to restore it. */}
          {SHOW_ENTERPRISE_BANNER && !isPaid && (
            <div className="mb-10 relative rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5 p-3.5 md:p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-bold text-text-primary leading-tight">
                      {t('upgrade.enterprise')}
                    </h3>
                    <p className="text-xs text-text-secondary truncate">
                      {ENTERPRISE_FEATURES.slice(0, 4).join(' · ')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setEnterpriseOpen(true)}
                  className="flex-shrink-0 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  {t('upgrade.contactSales')}
                </button>
              </div>
            </div>
          )}

          {/* Reviews / FAQ / Terms — hidden on the paid-user credit top-up
              screen (everything from "What people say" downward). */}
          {!isPaid && (
            <>
              {/* Reviews */}
              <ReviewsSection />

              {/* FAQ */}
              <div className="mb-10">
                <h2 className="text-xl font-bold text-text-primary mb-4 text-center">
                  {t('subscription.faq', { ns: 'translation' })}
                </h2>
                <div className="space-y-2 max-w-2xl mx-auto">
                  {FAQS.map((faq) => (
                    <FaqItem key={faq.q} question={faq.q} answer={faq.a} />
                  ))}
                </div>
              </div>

              {/* Terms */}
              <div className="text-center">
                <p className="text-xs text-text-tertiary">
                  <a href="#" className="underline hover:text-text-secondary">
                    {t('upgrade.termsOfService')}
                  </a>
                  {' & '}
                  <a href="#" className="underline hover:text-text-secondary">
                    {t('upgrade.privacyPolicy')}
                  </a>
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {enterpriseOpen && <EnterpriseModal onClose={() => setEnterpriseOpen(false)} />}
      {createTeamOpen && (
        <CreateTeamModal
          onClose={() => { setCreateTeamOpen(false); setTeamConfig(null); }}
          initial={teamConfig ?? undefined}
          skipPlanStep={!!teamConfig}
        />
      )}
      {paymentModal}
    </div>
  );
}
